import { Formik } from 'formik';
import { ProjectPageProjectFragment, SupportedLocale } from 'generated/graphql';
import { FC } from 'react';
import {
  getSupportedLocaleMetadata,
  SupportedLocale as L10nSupportedLocale,
} from '@sz/l10n';
import { EditFormWrapper } from '@spotted-zebra-uk/ui-components';
import LanguageForm from '../ProjectLanguageForm/LanguageForm/LanguageForm';

export interface IProjectLanguageFormSubmitValues {
  locales: SupportedLocale[];
  primaryLocale: SupportedLocale;
}

export interface IProjectFormValues {
  projectLocales: SupportedLocale[];
  primaryLocale: SupportedLocale;
}

interface IProjectLanguageForm {
  project?: ProjectPageProjectFragment;
  onProjectSave: (input: IProjectLanguageFormSubmitValues) => void;
  onCancel: () => void;
  saveLoading?: boolean;
}

const ProjectLanguageForm: FC<IProjectLanguageForm> = ({
  project,
  onProjectSave,
  onCancel,
}) => {
  const handleSubmit = (values: IProjectFormValues) => {
    // Keep the primary locale consistent with the selected languages: if it is
    // no longer among them, fall back to the first selected locale.
    const primaryLocale = values.projectLocales.includes(values.primaryLocale)
      ? values.primaryLocale
      : values.projectLocales[0] ?? SupportedLocale.English;
    onProjectSave({
      locales: values.projectLocales,
      primaryLocale,
    });
  };

  const initialValues = getInitialValues(project);

  return (
    <EditFormWrapper
      onCancelClick={onCancel}
      title="Edit role languages"
      formId="project-language-form"
      className="project-language-form"
    >
      <Formik<IProjectFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, values, setFieldValue }) => {
          return (
            <form id="project-language-form" onSubmit={handleSubmit}>
              {project && <LanguageForm title="Languages" />}
              {project && (
                <label
                  htmlFor="project-primary-locale"
                  className="project-language-form__primary"
                >
                  Primary language
                  <select
                    id="project-primary-locale"
                    name="primaryLocale"
                    value={values.primaryLocale}
                    onChange={(e) =>
                      setFieldValue('primaryLocale', e.target.value)
                    }
                  >
                    {values.projectLocales.map((locale) => (
                      <option key={locale} value={locale}>
                        {
                          getSupportedLocaleMetadata(
                            L10nSupportedLocale[locale],
                          ).name.english
                        }
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </form>
          );
        }}
      </Formik>
    </EditFormWrapper>
  );
};

const getInitialValues = (
  project?: ProjectPageProjectFragment,
): IProjectFormValues => {
  const projectLocales = project?.locales || [SupportedLocale.English];

  return {
    projectLocales: projectLocales,
    primaryLocale: project?.primaryLocale || SupportedLocale.English,
  };
};

export default ProjectLanguageForm;
